import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { ModalWrapper } from '../modals/ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';
import { communicationService, EmailRecipient } from '../../services/communicationService';
import { getApiError } from '../../utils/apiError';
import { formatFileSize } from '../../utils/formatFileSize';

interface Props {
  open: boolean;
  onClose: () => void;
  /** The record this email belongs to. A lead and a patient compose
   *  identically - same box, same branded shell - so one dialog serves both
   *  rather than a second copy that drifts. */
  entity?: 'patient' | 'lead';
  patientId: number;
  patientName: string;
  /** The record's own address. Still the default recipient, and the fallback
   *  the box opens with while the full list is on its way. */
  patientEmail: string | null;
  onSent: () => void;
}

// Turn the plain-text compose box into safe HTML for the branded email shell.
const escapeHtml = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const toHtml = (text: string): string =>
  escapeHtml(text).replace(/\n/g, '<br />');

// Kept equal to communication_attachment_service on the API side, which has
// the last word. Checking here as well is so somebody learns their 40MB scan
// is too big before they wait for the upload, not after.
const MAX_FILES = 10;
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const MAX_TOTAL_BYTES = 20 * 1024 * 1024;
const ALLOWED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/heic',
  'text/plain',
  'text/csv',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

const ComposeEmailDialog: React.FC<Props> = ({
  open, onClose, entity = 'patient', patientId, patientName, patientEmail, onSent,
}) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState<globalThis.File[]>([]);
  const [recipients, setRecipients] = useState<EmailRecipient[]>([]);
  const [toEmail, setToEmail] = useState('');
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = () => { setSubject(''); setBody(''); setAttachments([]); };

  // Every address on file for this record - their own, their GP's, whatever
  // else the clinic keeps. Fetched on open rather than passed in: the list
  // screens that host this dialog do not load the configured fields the extra
  // addresses live in, and the API is the same list the send validates
  // against, so the picker cannot offer something the send would refuse.
  useEffect(() => {
    if (!open) return;
    const own: EmailRecipient[] = patientEmail
      ? [{ email: patientEmail, label: entity === 'lead' ? 'Lead' : 'Patient', source: 'primary' }]
      : [];
    setRecipients(own);
    setToEmail(patientEmail || '');

    let cancelled = false;
    const fetchRecipients = entity === 'lead'
      ? communicationService.getLeadEmailRecipients
      : communicationService.getEmailRecipients;
    fetchRecipients(patientId)
      .then((list) => {
        if (cancelled || !list?.length) return;
        setRecipients(list);
        // The record's own address stays the default; the picker is for
        // departing from it deliberately.
        setToEmail((prev) => (list.some((r) => r.email === prev) ? prev : list[0].email));
      })
      .catch(() => {
        // Leave the record's own address in place - one unreachable lookup
        // should not stop somebody emailing the patient.
      });
    return () => { cancelled = true; };
  }, [open, entity, patientId, patientEmail]);

  const selected = recipients.find((r) => r.email === toEmail);
  // A third party reads this email and everything attached to it, so say so
  // rather than relying on the sender reading the address.
  const isThirdParty = !!selected && selected.source !== 'primary';
  const canSend = !!toEmail;

  const handleFilesPicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files || []);
    // Clear the input either way, so picking the same file twice in a row
    // still fires a change event.
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (picked.length === 0) return;

    const accepted: globalThis.File[] = [];
    let total = attachments.reduce((sum, f) => sum + f.size, 0);
    for (const file of picked) {
      if (attachments.length + accepted.length >= MAX_FILES) {
        toast.error(`At most ${MAX_FILES} attachments per email`);
        break;
      }
      if (file.size > MAX_FILE_BYTES) {
        toast.error(`"${file.name}" is larger than ${formatFileSize(MAX_FILE_BYTES)}`);
        continue;
      }
      if (file.type && !ALLOWED_TYPES.includes(file.type)) {
        toast.error(`"${file.name}" is a file type that cannot be emailed`);
        continue;
      }
      if (total + file.size > MAX_TOTAL_BYTES) {
        toast.error(`Attachments cannot total more than ${formatFileSize(MAX_TOTAL_BYTES)}`);
        break;
      }
      total += file.size;
      accepted.push(file);
    }
    if (accepted.length) setAttachments((prev) => [...prev, ...accepted]);
  };

  const removeAttachment = (index: number) =>
    setAttachments((prev) => prev.filter((_, i) => i !== index));

  const handleSend = async () => {
    if (!canSend) return;
    if (!subject.trim()) { toast.error('Subject is required'); return; }
    if (!body.trim()) { toast.error('Message is required'); return; }
    try {
      setSending(true);
      const send = entity === 'lead'
        ? communicationService.sendLeadEmail
        : communicationService.sendEmail;
      await send(patientId, {
        subject: subject.trim(),
        body_html: toHtml(body),
        attachments,
        to_email: toEmail,
      });
      toast.success(attachments.length
        ? `Email sent with ${attachments.length} attachment${attachments.length === 1 ? '' : 's'}`
        : 'Email sent');
      reset();
      onSent();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to send email'));
    } finally {
      setSending(false);
    }
  };

  const title = `Send Email to ${patientName}`;

  return (
    <ModalWrapper
      isOpen={open}
      onClose={onClose}
      title={title}
      isLoading={sending}
      width="max-w-2xl"
      titleAlign="left"
      titleClassName="text-lg font-bold text-[#366A75] leading-tight"
    >
      <div className="space-y-4">
        {!canSend && (
          <div className="px-4 py-2 bg-red-100 border border-red-400 text-red-700 rounded">
            No email address on file for this {entity}
          </div>
        )}
        <div>
          <label className="block text-[#366A75] font-medium mb-2" htmlFor="compose-email-to">
            To
          </label>
          {recipients.length > 1 ? (
            <select
              id="compose-email-to"
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
              disabled={sending}
              className={POPUP_STYLES.input}
            >
              {recipients.map((r) => (
                <option key={r.email} value={r.email}>
                  {r.label}  ·  {r.email}
                </option>
              ))}
            </select>
          ) : (
            <div className="w-full px-4 py-2 rounded-lg bg-[#EBF5F5] border border-[#B4D9D4] text-[#366A75] break-all">
              {toEmail || '—'}
            </div>
          )}
          {isThirdParty && (
            <p className="text-xs text-amber-700 mt-1">
              Not the {entity}&apos;s own address — this goes to {selected?.label}, including
              anything you attach.
            </p>
          )}
        </div>
        <div>
          <label className="block text-[#366A75] font-medium mb-2">Subject</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={sending || !canSend}
            className={POPUP_STYLES.input}
          />
        </div>
        <div>
          <label className="block text-[#366A75] font-medium mb-2">Message</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={sending || !canSend}
            rows={10}
            className={`${POPUP_STYLES.input} resize-y`}
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-[#366A75] font-medium">Attachments</label>
            <span className="text-xs text-gray-500">
              Up to {MAX_FILES} files, {formatFileSize(MAX_FILE_BYTES)} each
            </span>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            id="compose-email-attachments"
            accept={ALLOWED_TYPES.join(',')}
            onChange={handleFilesPicked}
            disabled={sending || !canSend}
          />
          <label
            htmlFor="compose-email-attachments"
            className={`w-full px-4 py-2 rounded-lg border border-[#B4D9D4] bg-[#EBF5F5] flex items-center justify-between ${
              sending || !canSend
                ? 'opacity-50 cursor-not-allowed'
                : 'cursor-pointer hover:bg-[#D0E7E5]'
            }`}
          >
            <span className="text-[#366A75] text-sm">Attach files</span>
            <svg className="w-5 h-5 text-[#366A75]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
              />
            </svg>
          </label>
          {attachments.length > 0 && (
            <div className="mt-2 space-y-2">
              {attachments.map((file, index) => (
                <div
                  key={`${file.name}-${index}`}
                  className="bg-[#d4e8e5] rounded-xl px-3 py-2 flex items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#366A75] truncate">{file.name}</div>
                    <div className="text-xs text-gray-600">{formatFileSize(file.size)}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAttachment(index)}
                    disabled={sending}
                    title="Remove"
                    aria-label={`Remove ${file.name}`}
                    className="p-1.5 hover:bg-white/50 rounded transition-colors disabled:opacity-50 shrink-0"
                  >
                    <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex gap-4 justify-center pt-2">
          <button
            onClick={handleSend}
            disabled={sending || !canSend}
            className={POPUP_STYLES.button}
          >
            {sending ? 'Sending...' : 'Send Email'}
          </button>
          <button onClick={onClose} disabled={sending} className={POPUP_STYLES.button}>
            Cancel
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
};

export default ComposeEmailDialog;
